import api from "api"

// #222 / BUG-003 (2026-07-10): the Google Maps JavaScript SDK key was stripped from the client
// bundle (it shipped in every historical build and must be rotated by an owner). Until an owner
// supplies a new, domain-restricted key in REACT_APP_GOOGLE_MAPS_JS_KEY, the SDK does not load.
//
// MAPS_KEY_PRESENT (build-time) gates whether we even attempt to load the SDK / wrap a component
// in GoogleApiWrapper. hasGoogleMaps() (run-time) gates every `new window.google.maps.*`
// construction so a missing/failed SDK degrades gracefully instead of white-screening the Where
// flow. Place search + geocoding stay fully functional WITHOUT the SDK via the backend provider
// proxy (/v1/provider/places/*), which holds the real key server-side.
export const MAPS_KEY_PRESENT = !!process.env.REACT_APP_GOOGLE_MAPS_JS_KEY

export const hasGoogleMaps = (): boolean =>
  typeof window !== "undefined" && !!(window as any).google?.maps

// Proxy-backed place autocomplete. Returns Google-shaped prediction objects
// ({ description, place_id, structured_formatting }). The real key stays on the backend.
export const proxyAutocomplete = async (query: string): Promise<any[]> => {
  if (!query) return []
  try {
    const { data } = await api.client.get("/v1/provider/places/autocomplete", {
      params: { query },
    })
    const predictions = data?.predictions || []
    return predictions.map((p: any) => ({
      description: p.description,
      place_id: p.place_id,
      structured_formatting: p.structured_formatting,
    }))
  } catch (error) {
    console.error("proxyAutocomplete error", error)
    return []
  }
}

// Proxy-backed geocode. Pass `placeId` OR (`lat`,`lng`). Returns
// { address, lat, lng, placeId } | undefined. Mirrors the shape getCoords returns from the JS SDK
// so callers do not branch on which path produced the result.
export const proxyGeocode = async (
  placeId?: string | null,
  lat?: number,
  lng?: number
): Promise<
  { address: string; lat: number; lng: number; placeId: string } | undefined
> => {
  try {
    const params: Record<string, string> = placeId
      ? { place_id: placeId }
      : { address: `${lat},${lng}` }
    const { data } = await api.client.get("/v1/provider/places/geocode", {
      params,
    })
    const result = data?.results?.[0]
    if (!result) return
    const loc = result.geometry?.location || {}
    const rLat = typeof loc.lat === "function" ? loc.lat() : loc.lat
    const rLng = typeof loc.lng === "function" ? loc.lng() : loc.lng
    return {
      address: result.formatted_address,
      lat: rLat,
      lng: rLng,
      placeId: placeId || result.place_id || "",
    }
  } catch (error) {
    console.error("proxyGeocode error", error)
  }
}
